import React from 'react';

import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import structuredContentLarge from '../assets/images/structured-content-1536.webp';
import structuredContentSmall from '../assets/images/structured-content-768.webp';
import PageLinkItem from '../components/PageLinkItem';
import projects from '../data/Projects';

const deliveredProjects = projects.filter(
  (project) => project.attribution === 'Scaled Tech delivery'
);
const earlierProjects = projects.filter((project) => project.attribution === 'Earlier experience');
const selectedStudies = [
  ...deliveredProjects.map((project) => ({
    title: project.title,
    attribution: project.attribution,
    description: project.description,
    path: '/case-studies/venue-workflow',
    tags: project.tags,
  })),
  {
    title: 'Shared multi-site publishing',
    attribution: 'Delivered through our predecessor software practice',
    description:
      'One CMS gave staff a shared place to manage distinct branded sites, with separate content, navigation, editorial access, and multilingual publishing.',
    path: '/case-studies/multi-site-publishing',
  },
  {
    title: 'Multi-level approval for administrative briefings',
    attribution: 'Earlier public-sector work, client anonymous',
    description:
      'Briefings moved through department, division, and executive approval levels, with configurable approvers, notifications, and an auditable path to publication.',
    path: '/case-studies/staged-review',
  },
];

function Solutions() {
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 7 }, overflowWrap: 'anywhere', '& p': { maxWidth: '70ch' } }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 280px' },
          gap: 4,
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography
            component='h1'
            variant='h3'
            sx={{ fontWeight: 800, color: '#0B2545', fontSize: { xs: '2rem', md: '3rem' } }}
          >
            Selected work
          </Typography>
          <Typography sx={{ mt: 2, color: '#475569', maxWidth: '65ch' }}>
            Start with these examples of publishing and workflow tools. Each identifies who
            delivered the work; the archive below preserves earlier projects and collaborations.
          </Typography>
          <Button
            component={Link}
            to='/case-studies'
            sx={{ mt: 2 }}
          >
            Read the CMS case studies
          </Button>
        </Box>
        <Box
          component='figure'
          sx={{
            m: 0,
            maxWidth: 280,
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid rgba(11, 37, 69, 0.12)',
            bgcolor: '#F8FAFC',
          }}
        >
          <img
            src={structuredContentLarge}
            srcSet={`${structuredContentSmall} 768w, ${structuredContentLarge} 1536w`}
            sizes='280px'
            width={1536}
            height={1024}
            loading='lazy'
            decoding='async'
            alt='Concept illustration of content blocks assembled into several different page layouts.'
            style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
          />
          <Typography
            component='figcaption'
            variant='caption'
            sx={{ display: 'block', px: 2, py: 1.5, color: '#475569' }}
          >
            Reusable content structures
          </Typography>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='selected-case-studies'
      >
        <Typography
          id='selected-case-studies'
          component='h2'
          variant='h5'
          sx={{ fontWeight: 700, mb: 2 }}
        >
          Selected case studies
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {selectedStudies.map((study) => (
            <Box
              component='article'
              key={study.path}
              sx={{
                minWidth: 0,
                p: 3,
                bgcolor: '#FFFFFF',
                border: '1px solid rgba(11, 37, 69, 0.14)',
                borderRadius: 3,
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: '#005596', fontWeight: 700 }}
              >
                {study.attribution}
              </Typography>
              <Typography
                component='h3'
                variant='h6'
                sx={{ mt: 1, fontWeight: 700 }}
              >
                {study.title}
              </Typography>
              <Typography sx={{ mt: 2, color: '#475569' }}>{study.description}</Typography>
              <Button
                component={Link}
                to={study.path}
                sx={{ mt: 2 }}
                aria-label={`Read case study: ${study.title}`}
              >
                Read case study
              </Button>
              {'tags' in study && (
                <Typography
                  variant='body2'
                  sx={{ mt: 2, color: '#475569' }}
                >
                  Technologies used: {study.tags.join(', ')}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Typography sx={{ mt: 3, maxWidth: '70ch', color: '#475569' }}>
          Other earlier work includes{' '}
          <Link to='/case-studies/handbook-publishing'>authenticated handbook publishing</Link> and{' '}
          <Link to='/case-studies/configurable-content'>configurable content and forms</Link>. The
          case studies explain the contribution and supported results.
        </Typography>
        <Button
          component={Link}
          to='/cms-governance'
          sx={{ mt: 1 }}
        >
          Explore CMS governance
        </Button>
      </Box>

      <Box
        component='section'
        aria-labelledby='ongoing-development'
        sx={{ mt: 5, maxWidth: '70ch' }}
      >
        <Typography
          id='ongoing-development'
          component='h2'
          variant='h5'
          sx={{ fontWeight: 700 }}
        >
          Ongoing development
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{ mt: 2, fontWeight: 700 }}
        >
          InkFlow Studio
        </Typography>
        <Typography sx={{ mt: 1, color: '#475569' }}>
          A local editorial workspace for Markdown content and media, with preview and static
          export. Its transition from WordPress to Cloudflare publishing is still in progress.
        </Typography>
      </Box>

      <Box
        component='section'
        aria-labelledby='earlier-experience'
        sx={{ mt: 5 }}
      >
        <Typography
          id='earlier-experience'
          component='h2'
          variant='h5'
          sx={{ fontWeight: 700 }}
        >
          Earlier experience and collaborations
        </Typography>
        <Typography sx={{ mt: 2, maxWidth: '70ch', color: '#475569' }}>
          This archive records work from our principal&apos;s predecessor practice and earlier
          collaborations. Descriptions reflect the contribution made at the time; they do not
          establish that a site remains active or that its current version is our work. Historical
          client identities remain anonymous where publication has not been cleared.
        </Typography>
        <Typography sx={{ mt: 2, maxWidth: '70ch', color: '#475569' }}>
          Earlier property marketing administration also used a central hub for separate property
          sites, with per-site configuration, language content, domains, and scoped editor roles.
        </Typography>
        <Box sx={{ mt: 3 }}>
          {earlierProjects.map((project) => (
            <Box
              component='article'
              key={project.title}
              sx={{ py: 3, borderTop: '1px solid rgba(11, 37, 69, 0.14)' }}
            >
              <Typography
                component='h3'
                variant='h6'
                sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}
              >
                {project.title}
              </Typography>
              <Typography sx={{ mt: 1, maxWidth: '70ch', color: '#475569' }}>
                {project.description}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1, maxWidth: '70ch', color: '#475569' }}
              >
                Technologies used at the time: {project.tags.join(', ')}
              </Typography>
              {project.link && (
                <PageLinkItem
                  third={project.link}
                  url={project.link}
                  type='pageLink'
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
}

export default Solutions;
